<template>
  <form @submit.prevent="submit">
    <div class="card">
      <div class="card-header">
        <div class="card-header-title">
          Κάρτες λίστας <span class="tag">Προσθήκη Προϊόντων</span>
        </div>

        <div class="card-header-icon">
          <RouterLink
            :to="{
              name: 'content.home.product-lists.items',
              params: { id: listId },
            }"
            class="button"
          >
            <span class="icon is-small"><i class="fa fa-list"/></span>
            <span>Λίστα</span>
          </RouterLink>
        </div>
      </div>

      <div class="card-content">
        <div class="field">
          <label class="label" title="Μέγιστο 16 ενεργά προϊόντα ανά λίστα">
            Εμφανές για τα νέα προϊόντα
          </label>
          <div class="control">
            <ToggleButton
              :sync="true"
              :value="form.is_active"
              :color="{ checked: '#22A684', unchecked: '#ff3860' }"
              :width="30"
              :height="15"
              @change="onToggleActive"
            />
          </div>
          <p class="help">
            Μέγιστο 16 ενεργά προϊόντα ανά λίστα.
            <br />
            <span class="has-text-grey">
              Τρέχοντα ενεργά:
              <strong>{{ activeItems }}</strong>
              <br />
              Διαθέσιμες θέσεις:
              <strong>{{ remainingActiveSlots }}</strong>
              <br />
              Σύνολο στη λίστα:
              <strong>{{ totalItems }}</strong>
            </span>
          </p>
        </div>
      </div>
    </div>

    <ToggleableCard :is-open="true" title="Συνδέσεις">
      <div class="card-content">
        <div class="columns is-multiline">
          <div class="column is-full">
            <label class="label">Προϊόντα</label>

            <!-- Uses your existing Multiselect-based ProductsSearch -->
            <ProductsSearch
              :products="selectedProducts"
              :required="true"
              @selectProduct="onSelectProduct"
              @removeProduct="onRemoveProduct"
            />

            <p class="help mt-2">
              <template v-if="form.is_active">
                Επιλέξτε έως
                <strong>{{ remainingActiveSlots }}</strong>
                προϊόντα για να είναι «Εμφανή» (υπόλοιπο ενεργών θέσεων).
              </template>
              <template v-else>
                Επιλέξτε προϊόντα για προσθήκη ως «Μη εμφανή».
              </template>
            </p>
          </div>

          <div class="column is-full">
            <div
              class="is-flex is-justify-content-space-between is-align-items-center"
            >
              <div class="is-size-6">
                Επιλεγμένα:
                <strong>
                  <template v-if="form.is_active">
                    {{ selectedProducts.length }} / {{ remainingActiveSlots }}
                  </template>
                  <template v-else>
                    {{ selectedProducts.length }}
                  </template>
                </strong>
              </div>
              <button
                :disabled="selectedProducts.length === 0"
                type="button"
                class="button is-small"
                @click="clearSelected"
              >
                Καθαρισμός
              </button>
            </div>
          </div>
        </div>
      </div>
    </ToggleableCard>

    <div class="card">
      <div class="card-content">
        <FormSubmit :is-saving="isSaving" @reset="reset" />
      </div>
    </div>
  </form>
</template>

<script>
import request from '@/utils/request';
import FormSubmit from '@/views/components/FormSubmit';
import ToggleableCard from '@/views/components/ToggleableCard';
import ProductsSearch from '@/views/components/ProductsSearch';

const MAX_ACTIVE_ITEMS = 16;
// Optional per-submit soft cap when not adding as active; keep it generous.
const MAX_ITEMS_PER_SUBMIT = 50;

export default {
  name: 'ProductListsItemsAdd',
  components: { FormSubmit, ToggleableCard, ProductsSearch },
  props: {
    id: { type: [String, Number], required: true },
  },

  data() {
    return {
      form: {
        is_active: true,
      },
      selectedProducts: [],
      isSaving: false,
      totalItems: 0,
      activeItems: 0,
      isLoadingCounts: false,
    };
  },

  computed: {
    listId() {
      return Number(this.id);
    },
    remainingActiveSlots() {
      const remain = MAX_ACTIVE_ITEMS - this.activeItems;
      return remain > 0 ? remain : 0;
    },
    currentSelectCap() {
      return this.form.is_active
        ? this.remainingActiveSlots
        : MAX_ITEMS_PER_SUBMIT;
    },
  },

  watch: {
    id: {
      immediate: true,
      handler() {
        this.fetchCounts();
      },
    },
  },

  methods: {
    async fetchCounts() {
      try {
        this.isLoadingCounts = true;
        const { data } = await request.get(
          `/home/product-lists/${this.listId}/items`,
        );

        // normalize response without nested ternaries
        let list = [];
        if (Array.isArray(data)) {
          list = data;
        } else if (data && Array.isArray(data.items)) {
          list = data.items;
        }

        this.totalItems = list.length;
        this.activeItems = list.filter(it => !!it.is_active).length;
      } catch (err) {
        this.totalItems = 0;
        this.activeItems = 0;
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'Αποτυχία φόρτωσης μετρητών λίστας',
        });
      } finally {
        this.isLoadingCounts = false;
      }
    },

    onToggleActive(v) {
      this.form.is_active = !!(v && v.value);
      // If switching to active and current selection exceeds the new cap, trim it.
      if (
        this.form.is_active &&
        this.selectedProducts.length > this.currentSelectCap
      ) {
        this.selectedProducts = this.selectedProducts.slice(
          0,
          this.currentSelectCap,
        );
        this.$notify({
          type: 'warning',
          title: 'Προσοχή',
          text:
            'Περικόπηκαν οι επιλογές ώστε να τηρηθούν οι διαθέσιμες ενεργές θέσεις.',
        });
      }
    },

    onSelectProduct(prod) {
      if (!prod) return;

      const exists = this.selectedProducts.some(p => p.id === prod.id);
      if (exists) return;

      if (
        this.currentSelectCap > 0 &&
        this.selectedProducts.length >= this.currentSelectCap
      ) {
        const baseMsg = this.form.is_active
          ? `Μέγιστο ${this.remainingActiveSlots} ενεργά προϊόντα μπορούν να προστεθούν αυτή τη στιγμή.`
          : `Μέγιστο ${MAX_ITEMS_PER_SUBMIT} προϊόντα ανά προσθήκη.`;
        this.$notify({
          type: 'warning',
          title: 'Προσοχή',
          text: baseMsg,
        });
        return;
      }

      this.selectedProducts = [...this.selectedProducts, prod];
    },

    onRemoveProduct(prod) {
      if (!prod) return;
      this.selectedProducts = this.selectedProducts.filter(
        p => p.id !== prod.id,
      );
    },

    clearSelected() {
      this.selectedProducts = [];
    },

    async submit() {
      if (!this.selectedProducts.length) {
        this.$notify({
          type: 'warning',
          title: 'Προσοχή',
          text: 'Επιλέξτε τουλάχιστον 1 προϊόν.',
        });
        return;
      }

      // Final guard: when adding as active, do not exceed remaining slots
      if (
        this.form.is_active &&
        this.selectedProducts.length > this.remainingActiveSlots
      ) {
        this.$notify({
          type: 'warning',
          title: 'Προσοχή',
          text:
            'Υπερβαίνετε τις διαθέσιμες ενεργές θέσεις. Μειώστε τις επιλογές σας.',
        });
        return;
      }

      this.isSaving = true;
      const productIds = this.selectedProducts.map(p => p.id);

      try {
        await request.post(`/home/product-lists/${this.listId}/items`, {
          product_ids: productIds,
          is_active: !!this.form.is_active,
        });

        this.$router
          .push({
            name: 'content.home.product-lists.items',
            params: { id: this.listId },
          })
          .catch(() => {});
        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Τα προϊόντα προστέθηκαν',
        });
      } catch (err) {
        const msg =
          err?.response?.data?.message || err?.message || 'To αίτημα απέτυχε';
        const uiMsg = /16|Maximum of 16/i.test(msg)
          ? 'Μέγιστο 16 ενεργά προϊόντα ανά λίστα'
          : msg;
        this.$notify({ type: 'error', title: 'Αποτυχία', text: uiMsg });
      } finally {
        this.isSaving = false;
      }
    },

    reset() {
      this.form.is_active = true;
      this.clearSelected();
      this.fetchCounts();
    },
  },
};
</script>
